import { createSelector } from "reselect";

import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import { countMissingRequiredColumns, makeSelectRequiredColumns, makeSelectMappedFields } from "../../../utils/export";
import { getChangedTabNames, getTabsWithEmptyFields } from "../../../utils/utils";
import type { ExportName, ExportSetting } from "../../type";
import { selectCurrentExportTab } from "../common";
import { selectSalesforceLoadingState } from "../salesforceSelectors";
import {
    getAccountsSimilarwebFieldOptions,
    getCompanyAsLeadsSimilarwebFieldOptions,
    getLeadsSimilarwebFieldOptions,
    getSalesforceContactsSimilarwebFieldOptions,
} from "../exportSimilarwebFieldOptions";

const management = createStatePropertySelector(selectSalesIntelligenceState("management"));
const sfExport = createStatePropertySelector(management("salesforceExport"));

export const selectSfSettings = sfExport("settings");
export const selectIsLoadingSettings = sfExport("isLoadingSettings");
const selectPrevSettings = sfExport("prevSettings");
export const selectDeletableField = sfExport("deletableField");
export const selectExportingToSF = sfExport("exportingDomainsToSF");
export const selectNotification = sfExport("notification");
export const selectSfIsSavingSettings = sfExport("isSavingSettings");

export const selectSfCurrentSettings = createSelector(
    selectCurrentExportTab,
    selectSfSettings,
    (tabName, allSettings): ExportSetting[] => allSettings[tabName] || [],
);

const makeSelectSfMappings = (recordName: ExportName) =>
    createSelector(selectSfSettings, (settings) => settings[recordName]);

const selectSfAccountsMappings = makeSelectSfMappings("accounts");
const selectSfContactsMappings = makeSelectSfMappings("contacts");
const selectSfLeadsMappings = makeSelectSfMappings("leads");
const selectSfCompanyAsLeadsMappings = makeSelectSfMappings("companyAsLeads");

const selectSfAccountsMappedFields = makeSelectMappedFields(selectSfAccountsMappings);
const selectSfContactsMappedFields = makeSelectMappedFields(selectSfContactsMappings);
const selectSfLeadsMappedFields = makeSelectMappedFields(selectSfLeadsMappings);
const selectSfCompanyAsLeadsMappedFields = makeSelectMappedFields(selectSfCompanyAsLeadsMappings);

const selectSfColumns = sfExport("columns");

export const makeSelectSfColumns = (recordName: ExportName) =>
    createSelector(selectSfColumns, (columns) => columns[recordName]);

export const selectSfAccountsColumns = makeSelectSfColumns("accounts");
export const selectSfContactsColumns = makeSelectSfColumns("contacts");
export const selectSfLeadsColumns = makeSelectSfColumns("leads");
export const selectSfCompanyAsLeadsColumns = makeSelectSfColumns("companyAsLeads");

const selectSfRequiredAccountsColumns = makeSelectRequiredColumns(selectSfAccountsColumns);
const selectSfRequiredContactsColumns = makeSelectRequiredColumns(selectSfContactsColumns);
const selectSfRequiredLeadsColumns = makeSelectRequiredColumns(selectSfLeadsColumns);
const selectSfRequiredCompanyAsLeadsColumns = makeSelectRequiredColumns(selectSfCompanyAsLeadsColumns);

export const selectSfAccountsMissingRequiredFieldsCount = createSelector(
    selectSfRequiredAccountsColumns,
    selectSfAccountsMappedFields,
    countMissingRequiredColumns,
);

export const selectSfContactsMissingRequiredFieldsCount = createSelector(
    selectSfRequiredContactsColumns,
    selectSfContactsMappedFields,
    countMissingRequiredColumns,
);

export const selectSfLeadsMissingRequiredFieldsCount = createSelector(
    selectSfRequiredLeadsColumns,
    selectSfLeadsMappedFields,
    countMissingRequiredColumns,
);

export const selectSfCompanyAsLeadsMissingRequiredFieldsCount = createSelector(
    selectSfRequiredCompanyAsLeadsColumns,
    selectSfCompanyAsLeadsMappedFields,
    countMissingRequiredColumns,
);

export const selectSfMissingRequiredFieldsCount = createSelector(
    selectSfAccountsMissingRequiredFieldsCount,
    selectSfContactsMissingRequiredFieldsCount,
    selectSfLeadsMissingRequiredFieldsCount,
    selectSfCompanyAsLeadsMissingRequiredFieldsCount,
    (accounts, contacts, leads, companyAsLeads) => ({ accounts, contacts, leads, companyAsLeads }),
);

export const selectSfPrevSettingsByCurrentType = createSelector(
    selectCurrentExportTab,
    selectPrevSettings,
    (tabName, allPrevSettings): ExportSetting[] => allPrevSettings[tabName] || [],
);

export const selectTotalSettings = createSelector(selectSfCurrentSettings, (settings): number => settings.length);

export const selectSfTabsWithEmptyFields = createSelector(
    selectSfSettings,
    selectIsContactsVisible,
    (allExportSettings, isContactsVisible): ExportName[] =>
        getTabsWithEmptyFields({ allExportSettings, isContactsVisible }),
);

export const selectSfIsTabsWithEmptyFields = createSelector(
    selectSfTabsWithEmptyFields,
    (tabsWithEmptyFields) => !!tabsWithEmptyFields.length,
);

export const selectSfSettingsChangedTabNames = createSelector(selectSfSettings, selectPrevSettings, getChangedTabNames);

export const selectIsSfSettingsChanged = createSelector(
    selectSfSettingsChangedTabNames,
    (tabNames) => !!tabNames.length,
);

export const selectSfNotSelectedSettings = getAccountsSimilarwebFieldOptions;

export const selectSfContactsNotSelectedSettings = getSalesforceContactsSimilarwebFieldOptions;

export const selectSfLeadsNotSelectedSettings = getLeadsSimilarwebFieldOptions;

export const selectSfCompanyAsLeadsNotSelectedSettings = getCompanyAsLeadsSimilarwebFieldOptions;

export const selectSfIsColumnsLoading = sfExport("isColumnsLoading");

export const selectIsLoadingSfExportPreferences = createSelector(
    selectSfIsColumnsLoading,
    selectIsLoadingSettings,
    selectSalesforceLoadingState,
    (isLoadingColumns, isLoadingSettings, isLoadingStatus) => {
        return isLoadingColumns || isLoadingSettings || isLoadingStatus;
    },
);
